import React, { useEffect, useRef, useState } from 'react';
import {
  Badge, Box, Button, Checkbox, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter,
  ModalHeader, ModalOverlay, Select, Stack, Text, useDisclosure, useToast, Alert, AlertIcon, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiEye, FiDownload, FiPlus, FiFileText, FiArchive, FiUpload,
} from 'react-icons/fi';
import { NIL } from 'uuid';
import IconProposals from '../assets/Icon-Proposals.svg';
import DocumentGridShell from './DocumentGridShell';
import { Proposal } from '../models/proposal';
import { CustomerType } from '../models/contactInfo'; // item 1386
import {
  END_USER_CONTRACT_TYPES, ContractType, CONTRACT_TYPE_LABEL, CONTRACT_STATUS_LABEL, EEULA_STATUS_LABEL, ContractStatus,
} from '../models/contract';
import {
  useProjectContracts, useGenerateContract, previewContract, openContractPdf, downloadContractPdf, downloadContractDocx, useArchiveContract,
  useFinalizeContract, useReopenContract, useEligibleContractProposals,
  useSendContract, useRefreshContractStatus, useUploadContract, useRegenerateContract,
  useContractSignWellEditLink, useUnsendContract,
} from '../hooks/useContracts';
import useCurrentUser from '../hooks/useCurrentUser';
import ContractBodyModal from './ContractBodyModal';
import DealTotalsCard from './DealTotalsCard'; // item 1109
import { useConfirm } from './ConfirmProvider';
import { SqlDateTimeToDisplay } from '../utils/SqlTools';
import usePortalShares from '../hooks/usePortalShares'; // item 997
import SharedToPortalChip from './SharedToPortalChip'; // item 997
import { previewPdf } from '../utils/openPdf'; // item 1010 — the internal EEULA view

// item 719 (RNWY Contracts epic S4) — generate + list contracts on the project. Admin-only. A deal's
// contract is built from an approved proposal (the job code = its WO#); PHDA + onboarding are
// customer-level (S8) so they aren't offered here.
// item 910 — badge color per review-lifecycle status
const statusScheme = (status: ContractStatus): string => {
  if (status === ContractStatus.Executed) return 'green';
  if (status === ContractStatus.Finalized) return 'purple';
  if (status === ContractStatus.SignWellDraft) return 'orange'; // item 1123 — action still needed
  return 'gray';
};

const PROJECT_TYPES: ContractType[] = [
  ContractType.Perpetual, ContractType.Subscription, ContractType.SwitchPilot,
  ContractType.HelpDeskOrder, ContractType.ThirdParty,
];
// item 952 (Chad) — a STANDARD proposal only generates HELP DESK paperwork (its pricing isn't
// engine-backed; the chain must always match: proposal = WO = invoice = contract). The server
// enforces the same rule.
const STANDARD_TYPES: ContractType[] = [ContractType.HelpDeskOrder];
// item 1386 (DIRECT END USERS epic) — the CRM contact TYPE picks the channel: an End User contact
// papers with the two direct-channel documents ONLY (SwitchPilot agreement with the EEULA inline +
// the all-in-one Help Desk Agreement); an Integrator contact keeps today's set. Never both — the
// server refuses cross-channel kinds too.
const END_USER_TYPES: ContractType[] = END_USER_CONTRACT_TYPES; // item 1393 — the full direct set

const ProjectContractsSection: React.FC<{
  projectId: string;
  proposals?: Proposal[];
  collapse?: { collapsed: boolean; onToggle: () => void };
  customerType?: number; // item 1386 — the project's customer TYPE (CustomerType enum) picks the channel
}> = ({
  projectId, proposals, collapse, customerType,
}) => {
  const isEndUser = customerType === CustomerType.EndUser; // item 1386
  const { data, status } = useProjectContracts(projectId === NIL ? undefined : projectId);
  const { isAdmin, isSuperAdmin } = useCurrentUser();
  const { capsFor } = usePortalShares(); // item 997 — badge rows the customer portal can see
  const generate = useGenerateContract();
  const archiver = useArchiveContract(); // item 906
  const finalizer = useFinalizeContract(); // item 910
  const reopener = useReopenContract(); // item 910
  const [adjustingId, setAdjustingId] = useState<string | null>(null); // item 910 — review editor
  const sender = useSendContract();
  const uploader = useUploadContract(); // item 977
  const rowFill = useColorModeValue('blackAlpha.50', 'whiteAlpha.50'); // item 958 — light-mode contrast (row tint vanished on light)
  const uploadRef = useRef<HTMLInputElement>(null);
  const refresher = useRefreshContractStatus();
  const editLinker = useContractSignWellEditLink(); // item 1122 — resume an abandoned send
  const unsender = useUnsendContract(); // item 1122 — cancel it (SignWell draft only)
  const regenerator = useRegenerateContract(); // item 1105
  const confirmDialog = useConfirm();
  const modal = useDisclosure();
  const toast = useToast();
  // item 1409 (Chad: "how do I fix this error, no way to edit… do I use Adjust? I'm confused") —
  // blanks a rebuild can't fill open a small FILL-IN dialog (the same blanks the generate modal
  // asks), pre-filled with the deal's suggestions; submit re-runs the rebuild with the answers.
  const [regenFill, setRegenFill] = useState<{ id: string; fills: string[]; values: Record<string, string> } | null>(null);
  const runRegenerate = async (id: string, fillValues?: Record<string, string>) => {
    const res: any = await regenerator.mutateAsync({ id, fillValues });
    const left: string[] = res?.data?.fills ?? [];
    const suggestions: Record<string, string> = res?.data?.suggestions ?? {};
    if (left.length > 0) {
      setRegenFill({ id, fills: left, values: Object.fromEntries(left.map((l) => [l, fillValues?.[l] ?? suggestions[l] ?? ''])) });
      return false;
    }
    setRegenFill(null);
    toast({ status: 'success', title: 'Contract rebuilt with the deal’s current numbers.' });
    return true;
  };
  // item 1006 (Chad) — NO internal EEULA send: it's the integrator's portal-only action.

  const sendForSignature = async (id: string) => {
    try {
      const r = await sender.mutateAsync(id);
      if (r.editUrl) window.open(r.editUrl, '_blank');
      // item 1123 — honest wording: nothing is SENT until it goes out from SignWell's editor
      toast({ status: 'success', title: 'SignWell draft created', description: 'Place the signature fields and hit send in SignWell — the status flips to SIGNWELL SENT once it actually goes out (customer signs first, then you).' });
    } catch (e: any) {
      toast({ status: 'error', title: 'Send failed', description: e?.response?.data ?? 'SignWell may not be configured.' });
    }
  };
  const [proposalId, setProposalId] = useState('');
  const [type, setType] = useState<ContractType>(ContractType.Subscription);
  // item 1386 — an End User project starts on its own channel's paper
  useEffect(() => {
    if (isEndUser && !END_USER_TYPES.includes(type)) setType(ContractType.EndUserSwitchPilot);
    if (!isEndUser && customerType != null && END_USER_TYPES.includes(type)) setType(ContractType.Subscription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEndUser, customerType]);
  const [missing, setMissing] = useState<string[]>([]);
  // item 901 — the deal term is CONFIRMED here (drives {{deal.termMonths}} + which monthly
  // fee merges); defaults from the picked proposal's sale type. 2 = TwoYear, 3 = FiveYear.
  const [termMonths, setTermMonths] = useState<number>(24);
  // item 1411 — the Help Desk tier the customer chose, asked only when the picked proposal never
  // recorded one (0 = None); the server writes it onto the proposal before merging
  const [helpDeskTier, setHelpDeskTier] = useState<number>(0);
  const HELP_DESK_TYPES: ContractType[] = [ContractType.HelpDeskPhda, ContractType.HelpDeskOrder, ContractType.EndUserHelpDesk];
  const pickedProposal: any = (proposals ?? []).find((x) => x.id === proposalId);
  const needsHelpDeskTier = HELP_DESK_TYPES.includes(type) && !!proposalId && !(pickedProposal?.rnwyHelpDeskTier > 0);
  // item 902 — values for the attorney's [FILL: ...] template blanks, prompted on demand
  const [fills, setFills] = useState<string[]>([]);
  const [fillValues, setFillValues] = useState<Record<string, string>>({});
  const termedType = type === ContractType.Subscription || type === ContractType.SwitchPilot
    || type === ContractType.EndUserSwitchPilot || type === ContractType.EndUserSubscription; // items 1386/1393
  // item 926 (Chad) — only PROCESSED proposals (guided flow through the finalized deposit
  // invoice) are offered: everything the contract needs then exists to auto-fill.
  // item 1178 (Chad) — LEGACY PAPER: include unprocessed proposals (contract for a legacy
  // deal without running WO/invoice scaffolding). Proposal stays mandatory — it's the
  // scope+cost source; whatever processing would have filled arrives as [FILL] prompts.
  const [legacyMode, setLegacyMode] = useState(false);
  const { data: eligibleData } = useEligibleContractProposals(projectId === NIL ? undefined : projectId, legacyMode);
  const rnwyProposals = eligibleData?.index ?? [];

  const pickProposal = (id: string) => {
    setProposalId(id);
    const p: any = (proposals ?? []).find((x) => x.id === id);
    // RnwySaleType: 2 = TwoYear, 3 = FiveYear (see models/proposal)
    if (p?.rnwySaleType === 3) setTermMonths(60);
    else if (p?.rnwySaleType === 2) setTermMonths(24);
    // item 952 — standard proposal picked → snap the type onto Help Desk paperwork
    // item 1431 — unless LEGACY PAPER is on: the admin types the money off the old paper
    const eligibleRow = (rnwyProposals ?? []).find((x) => x.id === id);
    if (eligibleRow && eligibleRow.isRnwy === false && !legacyMode && !STANDARD_TYPES.includes(type)) {
      // item 1386 — on the End User channel the help-desk paper is the all-in-one agreement
      setType(isEndUser ? ContractType.EndUserHelpDesk : ContractType.HelpDeskOrder);
    }
  };
  const buildInput = () => ({
    type,
    proposalId: proposalId || undefined,
    termMonths: termedType ? termMonths : undefined,
    helpDeskTier: needsHelpDeskTier && helpDeskTier > 0 ? helpDeskTier : undefined, // item 1411
    fillValues,
    // item 1178 — explicit legacy generate for an unprocessed pick (server re-checks tier)
    legacyOverride: legacyMode
      && (rnwyProposals ?? []).find((x) => x.id === proposalId)?.processed === false,
  });

  // item 940 (Chad) — self-refresh: when the section loads, poll SignWell for rows that are out
  // for signature (status updates) and for EXECUTED rows (backfills the SIGNED PDF into the file
  // store if it isn't filed yet — server no-ops once it is). Once per mount.
  const autoRefreshed = useRef(false);
  useEffect(() => {
    if (autoRefreshed.current || status !== 'success') return;
    const targets = (data?.index ?? []).filter(
      // item 1123 — SignWellDraft polls too, so the row flips to SIGNWELL SENT by itself
      // once the admin actually sends from SignWell's editor
      // item 1183-4c — uploaded executed paper has no SignWell envelope; polling it is a
      // guaranteed 404, so it never joins the auto-refresh set
      (c) => !(c as any).isUploaded
        && (c.status === ContractStatus.Sent || c.status === ContractStatus.Executed
        || c.status === ContractStatus.SignWellDraft),
    );
    if (targets.length === 0) return;
    // item 966 — throttle: each contract's SignWell poll runs at most once per 10 minutes
    // per browser session (every section mount was a SignWell round-trip per row).
    const now = Date.now();
    const throttled = targets.filter((c) => {
      const k = `basix:signwell-refresh:${c.id}`;
      try {
        const last = Number(window.sessionStorage.getItem(k) || 0);
        if (now - last < 10 * 60_000) return false;
        window.sessionStorage.setItem(k, String(now));
      } catch { /* no storage — poll anyway */ }
      return true;
    });
    if (throttled.length === 0) { autoRefreshed.current = true; return; }
    autoRefreshed.current = true;
    throttled.forEach((c) => refresher.mutate(c.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, data]);

  const rows = data?.index ?? [];

  const doGenerate = async () => {
    setMissing([]);
    try {
      const res = await generate.mutateAsync(buildInput());
      if (res.templateEmpty) {
        toast({ status: 'warning', title: 'No template saved for this type', description: 'Add it in Settings → Contracts & Onboarding templates.' });
        return;
      }
      // item 902 — the template still has [FILL: ...] blanks: prompt for each, right here.
      // item 908 — arrive PRE-FILLED with deal-derived suggestions (user edits win).
      if (res.fills && res.fills.length) {
        setFills(res.fills);
        if (res.fillDefaults) setFillValues((v) => ({ ...res.fillDefaults, ...v }));
      }
      if ((res.missing && res.missing.length) || (res.fills && res.fills.length)) {
        setMissing(res.missing ?? []);
        return;
      }
      toast({ status: 'success', title: 'Contract generated.' });
      modal.onClose();
    } catch {
      toast({ status: 'error', title: 'Could not generate the contract.' });
    }
  };

  return (
    <>
      <DocumentGridShell
        heading="CONTRACTS"
        icon={IconProposals}
        collapsible={!!collapse}
        collapsed={collapse?.collapsed}
        onToggleCollapse={collapse?.onToggle}
        status={status}
        data={status === 'success' ? rows : undefined}
        onAdd={isAdmin && projectId !== NIL ? () => { setMissing([]); setFills([]); setFillValues({}); modal.onOpen(); } : undefined}
      >
        <Stack spacing={2}>
          {rows.length === 0 && <Text color="gray.500" fontSize="sm">No contracts generated yet.</Text>}
          {/* item 1109 (Chad) — one DEAL TOTAL strip per proposal: what the customer signed up
              for across ALL of that deal's agreements, one-time vs monthly, with the
              reconciliation check against the quote. */}
          {Array.from(new Set(rows.map((r) => r.proposalId).filter(Boolean)))
            .map((pid) => <DealTotalsCard key={`deal-${pid}`} proposalId={pid as string} />)}
          {rows.map((c) => (
            /* item 1010 (Chad: "a button info forest") — mirror the PORTAL's split: the contract
               row on top, the END-USER LICENSE (EEULA) as a purple nested lane beneath it */
            <Box key={c.id} px={3} py={2} borderRadius="md" bg={rowFill}>
              <HStack spacing={4} wrap="wrap">
                <Box minW={0}>
                  <Text fontWeight="semibold" noOfLines={1}>{c.title}</Text>
                  <Text fontSize="xs" color="gray.500">
                    {`${CONTRACT_TYPE_LABEL[c.type]}${c.number ? ` · ${c.number}` : ''} · ${SqlDateTimeToDisplay(c.effectiveDate)}`}
                  </Text>
                </Box>
                <Badge colorScheme={statusScheme(c.status)}>
                  {CONTRACT_STATUS_LABEL[c.status]}
                </Badge>
                {/* item 997 (Chad) — internal awareness: sent/signed/executed contracts show in the
                    customer's portal (agreement row + EEULA loop; never the contract file itself) */}
                {[1, 2, 3].includes(c.status as number) && capsFor((c as any).customerId)?.contracts && (
                  <SharedToPortalChip what="agreements (status + EEULA loop)" />
                )}
                <HStack ml="auto" spacing={1} flexWrap="wrap" justify="flex-end" sx={{ rowGap: '4px' }}>
                  {/* item 910 — review lifecycle: Draft adjusts + finalizes; only Finalized sends */}
                  {c.status === ContractStatus.Draft && (
                    <>
                      <Button size="xs" variant="outline" onClick={() => setAdjustingId(c.id)}>Adjust</Button>
                      {/* item 1105 (Chad) — pull template/pricing fixes into a draft that was
                          generated before them; replaces manual edits, so it confirms first */}
                      <Button
                        size="xs"
                        variant="outline"
                        // item 1105b — PER-ROW spinner: one shared mutation lit up EVERY
                        // row's button (Chad: "all three buttons spin"), reading as if all
                        // three contracts were rebuilding. Only the clicked one is.
                        isLoading={regenerator.isLoading && regenerator.variables?.id === c.id}
                        onClick={async () => {
                          if (!(await confirmDialog(
                            'Rebuild this draft from the current template and the deal\u2019s numbers? Any manual edits you made in Adjust are replaced.',
                            { title: 'Regenerate contract', confirmLabel: 'Regenerate' },
                          ))) return;
                          try {
                            // item 1105c/1409 — a blank the deal can't answer opens the fill-in dialog
                            await runRegenerate(c.id);
                          } catch (e: any) {
                            toast({ status: 'error', title: 'Could not regenerate', description: e?.response?.data ?? 'Please try again.' });
                          }
                        }}
                      >
                        Regenerate
                      </Button>
                      <Button size="xs" colorScheme="brandGreen" isLoading={finalizer.isLoading && finalizer.variables === c.id} onClick={() => finalizer.mutate(c.id, { onError: (e: any) => { toast({ status: 'error', title: 'Could not finalize', description: String(e?.response?.data ?? 'Try again.') }); } })}>Finalize</Button>
                    </>
                  )}
                  {c.status === ContractStatus.Finalized && (
                    <>
                      <Button size="xs" colorScheme="purple" isLoading={sender.isLoading && sender.variables === c.id} onClick={() => sendForSignature(c.id)}>Send for signature</Button>
                      <Button size="xs" variant="ghost" isLoading={reopener.isLoading && reopener.variables === c.id} onClick={() => reopener.mutate(c.id, { onError: (e: any) => { toast({ status: 'error', title: 'Could not reopen', description: String(e?.response?.data ?? 'Try again.') }); } })}>Reopen</Button>
                    </>
                  )}
                  {(c.status === ContractStatus.Sent || c.status === ContractStatus.SignWellDraft) && (
                    <>
                      <Button size="xs" variant="ghost" isLoading={refresher.isLoading && refresher.variables === c.id} onClick={() => refresher.mutate(c.id)}>Refresh</Button>
                      {/* item 1122/1123 — a SIGNWELL DRAFT (envelope created, nobody emailed):
                          resume the editor or cancel the send. A stale SENT row from the old
                          optimistic send self-heals to SignWellDraft via Refresh. */}
                      {isAdmin && c.status === ContractStatus.SignWellDraft && (
                        <>
                          <Button
                            size="xs"
                            variant="outline"
                            isLoading={editLinker.isLoading && editLinker.variables === c.id}
                            onClick={async () => {
                              try {
                                const r = await editLinker.mutateAsync(c.id);
                                if (r.editUrl) window.open(r.editUrl, '_blank');
                              } catch (e: any) {
                                toast({ status: 'error', title: 'Could not open SignWell', description: String(e?.response?.data ?? 'Try again.') });
                              }
                            }}
                          >
                            Finish in SignWell
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            color="gray.500"
                            isLoading={unsender.isLoading && unsender.variables === c.id}
                            onClick={async () => {
                              if (!(await confirmDialog(
                                'Cancel this send? The SignWell draft is deleted (no emails ever went out) and the contract goes back to Finalized so you can reopen, regenerate, and send again.',
                                { title: 'Cancel send', confirmLabel: 'Cancel send' },
                              ))) return;
                              unsender.mutate(c.id, {
                                onSuccess: () => { toast({ status: 'success', title: 'Send canceled', description: 'Back to Finalized — reopen it to regenerate before resending.' }); },
                                onError: (e: any) => { toast({ status: 'error', title: 'Could not cancel', description: String(e?.response?.data ?? 'Try again.') }); },
                              });
                            }}
                          >
                            Cancel send
                          </Button>
                        </>
                      )}
                    </>
                  )}
                  <Button size="xs" variant="ghost" leftIcon={<FiEye />} onClick={() => openContractPdf(c.id).catch(() => toast({ status: 'error', title: 'Could not open the PDF.' }))}>Preview</Button>
                  <Button size="xs" variant="ghost" leftIcon={<FiDownload />} onClick={() => downloadContractPdf(c.id, c.title || 'contract').catch(() => toast({ status: 'error', title: 'Could not download the PDF.' }))}>Download</Button>
                  {/* item 904 — Word rendition for attorney redlines */}
                  <Button size="xs" variant="ghost" leftIcon={<FiFileText />} onClick={() => downloadContractDocx(c.id, c.title || 'contract').catch(() => toast({ status: 'error', title: 'Could not download the Word file.' }))}>Word</Button>
                  {/* item 906 — archive a stale contract (super-admin; frozen-PDF drafts etc.) */}
                  {isSuperAdmin && c.status !== ContractStatus.Sent && c.status !== ContractStatus.SignWellDraft && (
                    <Button size="xs" variant="ghost" color="gray.500" leftIcon={<FiArchive />} isLoading={archiver.isLoading && archiver.variables === c.id} onClick={() => archiver.mutate(c.id, { onError: (e: any) => { toast({ status: 'error', title: 'Could not archive', description: String(e?.response?.data ?? 'Try again.') }); } })}>Archive</Button>
                  )}
                </HStack>
              </HStack>
              {/* item 1010 — the END-USER LICENSE (EEULA) lane, quiet + read-only internally
                  (the send lives in the integrator's portal, item 1006) */}
              {(c.eeulaStatus ?? 0) > 0 && (
              <Box mt={2} ml={{ base: 0, md: 5 }} pl={3} py={2} pr={2} borderLeftWidth="3px" borderLeftColor="purple.400" borderRadius="md" bg="rgba(99, 50, 244, 0.08)">
                <HStack spacing={3} wrap="wrap">
                  <Box minW={0}>
                    <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em" color="purple.300">
                      END-USER LICENSE (EEULA)
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {(() => {
                        if (c.eeulaStatus === 3) return `Accepted by ${c.eeulaAcceptedName ?? '—'}${c.eeulaAcceptedAt ? ` on ${SqlDateTimeToDisplay(c.eeulaAcceptedAt)}` : ''}`;
                        if (c.eeulaStatus === 2) return `Sent to ${c.eeulaSentToName ?? ''} (${c.eeulaSentToEmail ?? ''})${c.eeulaLastSentAt ? ` · last nudged ${SqlDateTimeToDisplay(c.eeulaLastSentAt)}` : ''}`;
                        if (c.eeulaStatus === 4) return 'Legacy agreement — the integrator adds its EEULA text in their portal.';
                        if (c.eeulaStatus === 5) return 'Acceptance confirmed by the integrator (self-attested).';
                        return 'Not sent yet — the integrator sends it from their portal.';
                      })()}
                    </Text>
                  </Box>
                  {(() => {
                    const scheme = {
                      3: 'green', 5: 'green', 2: 'orange', 1: 'red',
                    } as Record<number, string>;
                    return (
                      <Badge colorScheme={scheme[c.eeulaStatus ?? 0] ?? 'gray'} variant="subtle">
                        {EEULA_STATUS_LABEL[c.eeulaStatus ?? 0]}
                      </Badge>
                    );
                  })()}
                  <HStack ml="auto" spacing={1} flexWrap="wrap" justify="flex-end" sx={{ rowGap: '4px' }}>
                    {/* the EEULA document (led by the acceptance certificate once accepted) */}
                    {((c as any).isUploaded ? (c as any).hasEeulaOverride : true) && (
                      <Button
                        size="xs"
                        variant="ghost"
                        leftIcon={<FiEye />}
                        onClick={() => previewPdf(`Contracts/${c.id}/eeula-pdf`).catch(() => toast({ status: 'error', title: 'No EEULA document for this agreement yet.' }))}
                      >
                        EEULA (PDF)
                      </Button>
                    )}
                  </HStack>
                </HStack>
              </Box>
              )}
            </Box>
          ))}
        </Stack>
      </DocumentGridShell>

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Generate contract</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>Contract type</Text>
                <Select value={type} onChange={(e) => setType(Number(e.target.value))}>
                  {/* item 1386 — the contact TYPE picks the channel; a standard proposal narrows to help desk */}
                  {(() => {
                    // item 1431 — Legacy paper lifts the 952 narrowing (server enforces the same rule)
                    const standard = (rnwyProposals ?? []).find((x) => x.id === proposalId)?.isRnwy === false && !legacyMode;
                    if (isEndUser) return standard ? [ContractType.EndUserHelpDesk] : END_USER_TYPES;
                    return standard ? STANDARD_TYPES : PROJECT_TYPES;
                  })()
                    .map((t) => <option key={t} value={t}>{CONTRACT_TYPE_LABEL[t]}</option>)}
                </Select>
                {isEndUser && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    End User (direct) channel — the end user is the contracting party. Each software agreement
                    carries its EEULA as an exhibit, accepted by signature; nothing separate is sent.
                  </Text>
                )}
                {(rnwyProposals ?? []).find((x) => x.id === proposalId)?.isRnwy === false && (
                  <Text fontSize="xs" color="orange.300" mt={1}>
                    {/* item 1431 — legacy paper opens the full set; the money is typed off the old paper */}
                    {legacyMode
                      ? 'Standard proposal on legacy paper — any agreement; pricing the engine can\'t derive becomes fill-in prompts (type it off the old paper). The proposal is stamped PAPER ONLY.'
                      : 'Standard proposal — Help Desk paperwork only. Software agreements need an automated (engine-priced) proposal so the contract matches the invoices — or tick Legacy paper below.'}
                  </Text>
                )}
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={1}>From proposal (job code + pricing)</Text>
                <Select placeholder="Select a proposal…" value={proposalId} onChange={(e) => pickProposal(e.target.value)}>
                  {rnwyProposals.map((p) => (
                    <option key={p.id} value={p.id}>
                      {`${p.proposalNumber ?? ''} · ${p.proposalTitle ?? ''}${p.processed === false ? ' — UNPROCESSED (legacy)' : ''}`}
                    </option>
                  ))}
                </Select>
                {/* item 1178 — the deliberate escape hatch for legacy deals */}
                <Checkbox
                  mt={2}
                  size="sm"
                  isChecked={legacyMode}
                  onChange={(e) => {
                    setLegacyMode(e.target.checked);
                    // item 1431 — legacy OFF again with a software type on a standard pick → snap back (952)
                    if (!e.target.checked) {
                      if (termMonths !== 24 && termMonths !== 60) setTermMonths(24); // item 1435 — back to the 2-/5-year pick
                      const std = (rnwyProposals ?? []).find((x) => x.id === proposalId)?.isRnwy === false;
                      if (std && !STANDARD_TYPES.includes(type) && type !== ContractType.EndUserHelpDesk) {
                        setType(isEndUser ? ContractType.EndUserHelpDesk : ContractType.HelpDeskOrder);
                      }
                    }
                  }}
                  colorScheme="purple"
                >
                  {/* item 1192 (Chad's UX bug) — the gray-on-gray label read as DISABLED;
                      the action reads normal-weight now, the parenthetical stays quiet */}
                  <Text as="span" fontSize="xs">
                    Legacy paper — include unprocessed proposals
                    <Text as="span" color="gray.500"> (missing WO/invoice data becomes fill-in prompts)</Text>
                  </Text>
                </Checkbox>
                {rnwyProposals.length === 0 && (
                  <Text fontSize="xs" color="orange.300" mt={1}>
                    {/* item 1192 — the nudge acknowledges legacy mode instead of preaching the old law */}
                    {legacyMode
                      ? 'No proposals on this project at all — build one first (it can stay PAPER ONLY).'
                      : 'No processed proposals yet — run the guided flow through the FINALIZED deposit invoice, or tick Legacy paper above to include unprocessed proposals.'}
                  </Text>
                )}
              </Box>
              {/* item 1411 — Help Desk paper on a deal with no recorded tier: the customer has chosen by
                  now (contracts are post-acceptance), so ask once and record it on the proposal */}
              {needsHelpDeskTier && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={1}>Help Desk tier the customer chose (recorded on the proposal)</Text>
                  <Select value={helpDeskTier} onChange={(e) => setHelpDeskTier(Number(e.target.value))}>
                    <option value={0}>Pick a tier…</option>
                    <option value={1}>Gold</option>
                    <option value={2}>Platinum</option>
                    <option value={3}>Titanium</option>
                  </Select>
                </Box>
              )}
              {termedType && !legacyMode && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={1}>Deal term (confirm — drives the term + monthly fee)</Text>
                  <Select value={termMonths} onChange={(e) => setTermMonths(Number(e.target.value))}>
                    {/* item 1407b (Chad): SwitchPilot follows standard RNWY — 2- and 5-year only */}
                    <option value={24}>2-year — twenty-four (24) months</option>
                    <option value={60}>5-year — sixty (60) months</option>
                  </Select>
                </Box>
              )}
              {termedType && legacyMode && (
                /* item 1435 (Chad: "I need the flexibility to create a contract on the fly with a term")
                   — LEGACY PAPER takes the Initial Term as a typed number of months (12, 24, 36…).
                   The paper's Section 1 already auto-renews in twelve-month periods whatever the
                   first term is, and the first Periodic Increase notice prefills at term − 3 months
                   (overwritable in the blanks below). Automated deals keep the 2-/5-year pick. */
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={1}>Initial term (months) — legacy paper</Text>
                  <Input
                    type="number"
                    min={1}
                    max={120}
                    step={1}
                    value={termMonths}
                    onChange={(e) => {
                      const n = Math.floor(Number(e.target.value));
                      if (Number.isFinite(n) && n >= 1 && n <= 120) setTermMonths(n);
                    }}
                    w="160px"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Any whole number of months. The agreement then auto-renews in twelve (12) month periods (Section 1).
                    First Periodic Increase notice suggests term − 3 months — change it in the blanks below.
                  </Text>
                </Box>
              )}
              {fills.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={1}>Fill in the contract blanks</Text>
                  <Stack spacing={2}>
                    {fills.map((label) => (
                      <Box key={label}>
                        <Text fontSize="xs" mb={1}>{label}</Text>
                        <Input
                          size="sm"
                          value={fillValues[label] ?? ''}
                          placeholder={label}
                          onChange={(e) => setFillValues((v) => ({ ...v, [label]: e.target.value }))}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
              {missing.length > 0 && (
                <Alert status="warning" borderRadius="md" alignItems="flex-start" fontSize="sm">
                  <AlertIcon />
                  <Box>
                    <Text fontWeight="semibold">Fill these before generating:</Text>
                    <Text>{missing.join(' · ')}</Text>
                  </Box>
                </Alert>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter flexWrap="wrap" sx={{ gap: '8px', rowGap: '8px' }}>
            {/* item 977 — file an EXISTING signed contract under this project (portal-visible;
                EEULA reads UNKNOWN + self-attestable) */}
            <input
              ref={uploadRef}
              type="file"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (!f) return;
                try {
                  await uploader.mutateAsync({
                    customerId: NIL, projectId, type, file: f,
                  });
                  toast({ status: 'success', title: 'Signed contract filed', description: `${f.name} is on this project (EEULA status: unknown).` });
                  modal.onClose();
                } catch (err: any) {
                  toast({ status: 'error', title: 'Upload failed', description: String(err?.response?.data ?? 'Try again.') });
                }
              }}
            />
            <Button variant="outline" mr="auto" leftIcon={<FiUpload />} isLoading={uploader.isLoading} onClick={() => uploadRef.current?.click()}>
              Upload existing signed
            </Button>
            <Button variant="ghost" mr={2} onClick={() => previewContract(buildInput()).catch((e: any) => toast({ status: 'error', title: 'Preview failed', description: e?.message || 'Please try again.' }))} leftIcon={<FiEye />}>Preview</Button>
            <Button colorScheme="brandGreen" leftIcon={<FiPlus />} isLoading={generate.isLoading} onClick={doGenerate}>Generate</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ContractBodyModal contractId={adjustingId} onClose={() => setAdjustingId(null)} />
      {/* item 1409 — the rebuild's leftover blanks, answered here (never hunted for in Adjust) */}
      <Modal isOpen={!!regenFill} onClose={() => setRegenFill(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Fill in the contract blanks</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" opacity={0.75} mb={3}>
              The rebuild filled everything the deal knows. These few need you — the suggestions are pre-filled where we have one.
            </Text>
            <Stack spacing={2}>
              {(regenFill?.fills ?? []).map((label) => (
                <Box key={label}>
                  <Text fontSize="xs" mb={1}>{label}</Text>
                  <Input
                    size="sm"
                    value={regenFill?.values[label] ?? ''}
                    placeholder={label}
                    onChange={(e) => setRegenFill((s) => (s ? { ...s, values: { ...s.values, [label]: e.target.value } } : s))}
                  />
                </Box>
              ))}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setRegenFill(null)}>Later</Button>
            <Button
              colorScheme="brandGreen"
              isLoading={regenerator.isLoading}
              isDisabled={!regenFill || regenFill.fills.some((l) => !(regenFill.values[l] ?? '').trim())}
              onClick={async () => {
                if (!regenFill) return;
                try {
                  await runRegenerate(regenFill.id, regenFill.values);
                } catch (e: any) {
                  toast({ status: 'error', title: 'Could not regenerate', description: e?.response?.data ?? 'Please try again.' });
                }
              }}
            >
              Rebuild with these
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

ProjectContractsSection.defaultProps = { proposals: undefined, collapse: undefined, customerType: undefined };

export default ProjectContractsSection;
